import { Box, Button, Card, CardContent, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Grid, styled, Switch, Tooltip } from "@mui/material";
import { Ico_ArrowDown, Ico_ArrowRight, Ico_Warning } from "@sagecentral/core-ui";
import { TaskProgressPill } from "components/tasks";
import useWorkItemWorkflowsStatusChange from "hooks/work-items/useWorkItemWorkflowsStatusChange";
import { IWorkItemWorkflow } from "models/work-items";
import { useCallback, useEffect, useMemo, useState } from "react";
import WorkflowTasksGrid from "./WorkflowTasksGrid";

type WorkflowTasksCardProps = {
    workflow: IWorkItemWorkflow;
    canEdit: boolean;
    canActivate: boolean;
    canDeactivate: boolean;
    showAll?: boolean;
    isExpanded?: boolean;
    onExpandToggle?: (isExpanded: boolean) => void;
    refresh?: () => void;
    project?: boolean;
};

const StyledSwitch = styled(Switch)(({ theme }) => ({
    width: 65,
    height: 32,
    padding: 6,
    '&.Mui-focusVisible': {
        outline: 'none',
        '& .MuiSwitch-track': {
            boxShadow: `0 0 0 2px ${theme?.palette?.primary?.main || '#001A69'}`,
            borderRadius: '16px'
        }
    },
    '& .MuiSwitch-switchBase': {
        margin: 4,
        padding: 1,
        transform: "translateX(2px)",
        '&.Mui-checked': {
            transform: "translateX(30px)",
            '& .MuiSwitch-thumb:before': {
                backgroundColor: '#001A69'
            },
            '& + .MuiSwitch-track': {
                backgroundColor: '#FFFFFF',
                opacity: 1,
                borderColor: '#001A69'
            },
        },
        '&.Mui-disabled': {
            '& .MuiSwitch-thumb': {
                backgroundColor: '#BDBDBD',
                '&:before': {
                    borderColor: '#BDBDBD'
                }
            },
            '& + .MuiSwitch-track': {
                opacity: 0.5,
                backgroundColor: '#E0E0E0',
                borderColor: '#BDBDBD',
                "&:after, &:before": {
                    color: '#9E9E9E'
                }
            },
        },
        '&.Mui-checked.Mui-disabled': {
            transform: "translateX(30px)",
            '& .MuiSwitch-thumb:before': {
                backgroundColor: '#BDBDBD'
            },
            '& + .MuiSwitch-track': {
                opacity: 0.5,
                backgroundColor: '#E0E0E0',
                borderColor: '#BDBDBD',
            },
        },
    },
    '& .MuiSwitch-thumb': {
        width: 24,
        height: 22,
        backgroundColor: '#FF0000',
        '&:before': {
            content: '""',
            position: 'absolute',
            width: 24,
            height: 22,
            borderRadius: '50%',
            borderStyle: 'solid',
            borderWidth: '1px 1px 1px 1px',
            borderColor: '#001A69'
        },
    },
    '& .MuiSwitch-track': {
        borderRadius: 20 / 2,
        backgroundColor: 'white',
        borderStyle: 'solid',
        borderWidth: '1px 1px 1px 1px',
        opacity: 1,
        "&:after, &:before": {
            fontSize: "12px",
            position: "absolute",
            top: "7px",
            fontWeight: "bold",
            color: '#333333'
        },
        "&:after": {
            content: "'NO'",
            left: "35px",
        },
        "&:before": {
            content: "'YES'",
            right: "34px"
        }
    },
}));

const WorkflowTasksCard = (props: WorkflowTasksCardProps) => {
    const { 
        workflow,
        showAll,
        isExpanded,
        onExpandToggle,
        canActivate,
        canDeactivate,
        canEdit,
        refresh
    } = props;
    const [ expand, setExpand ] = useState(props.isExpanded ?? props.showAll ?? false);

    const handleStatusChange = useWorkItemWorkflowsStatusChange();
    
    const [ changeWorkflowStatus, setChangeWorkflowStatus ] = useState<boolean>(false);

    const workflowState = !workflow.status ? 'Activate': 'Deactivate';

    const handleConfirm = useCallback(async () => {
        setChangeWorkflowStatus(!changeWorkflowStatus);
        const result = await handleStatusChange(workflow.id, !workflow.status, workflow.name);
        if (result) {
            refresh?.();
        }
    }, [ handleStatusChange, workflow ]);

    useEffect(() => {
        if (isExpanded !== undefined) {
            setExpand(isExpanded);
        } else if (showAll !== undefined) {
            setExpand(showAll);
        }
    }, [isExpanded, showAll]);
    
    const toggleExpand = useCallback(() => {
        const newExpandState = !expand;
        setExpand(newExpandState);
        
        if (onExpandToggle) {
            onExpandToggle(newExpandState);
        }
    }, [expand, onExpandToggle]);
    const onStatusToggle = () => {
        setChangeWorkflowStatus(!changeWorkflowStatus);        
    };

    const editWorkflowUrl = useMemo(
        () => `${process.env.REACT_APP_CLASSIC_URL}/workflowinstance/editworkflow?workflowInstanceID=${workflow.id}`,
    [ workflow ]);

    return (
        <Card sx={{
            mb: 1
        }}>
            <CardContent>
                <Grid container>
                    <Grid item 
                        xs={3} sx={{ 
                            fontWeight: 'bold', 
                            pb: 1, 
                            display: 'flex', 
                            alignItems: 'center' 
                        }} 
                        onClick={toggleExpand}
                    >
                        <Button
                            aria-label={expand ? 'Collapse workflow details' : 'Expand workflow details'}
                            aria-expanded={expand}
                            onClick={() => setExpand(!expand)}
                            sx={{
                                minWidth: 'auto',
                                p: 0,
                                mr: 1,
                                display: 'flex',
                                alignItems: 'center',
                                color: 'primary.main',
                                '&:focus-visible': {
                                    outline: (theme) => `2px solid ${theme.palette?.primary?.main || '#001A69'}`,
                                    outlineOffset: '2px'
                                }
                            }}
                        >
                            { expand 
                                ? <Ico_ArrowDown color='primary'/> 
                                : <Ico_ArrowRight color='primary'/> }
                        </Button>
                        {workflow.name}
                    </Grid>
                    <Grid item xs={3} lg sx={{ fontWeight: 'bold', pb: 1, display: 'flex', alignItems: 'center' }}>

                        {canEdit && < div style={{ whiteSpace: 'pre-wrap', flexWrap: 'nowrap' }}>
                            <Button href={editWorkflowUrl} style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }} rel="noopener noreferrer">
                                Edit Workflow
                            </Button>
                        </div>}
                    </Grid>

                    <Grid item xs={3} lg={2} sx={{ fontWeight: 'bold', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        { 
                        !props.project &&
                        <Tooltip title={(canActivate && canDeactivate) || (canActivate && !workflow.status) || (canDeactivate && workflow.status) ? undefined 
                                    :`You don't have permissions to ${!canActivate && !workflow.status ? 'activate'
                                        : !canDeactivate && workflow.status ? 'deactivate'
                                        : !canActivate && !canDeactivate ? 'activate/deactivate'
                                        : ''} this workflow.`}>
                            <Box>Active: 
                            <StyledSwitch 
                                disabled={(!workflow.status && !canActivate) || (workflow.status && !canDeactivate)}
                                checked={workflow.status}
                                onChange={onStatusToggle}
                                inputProps={{
                                    'aria-label': workflow.status ? 'Workflow is active, click to deactivate' : 'Workflow is inactive, click to activate',
                                }}
                            />
                                        <Dialog fullWidth={false} maxWidth={'lg'} onClose={setChangeWorkflowStatus} open={changeWorkflowStatus}>
                                            <DialogTitle sx={{textAlign: 'left', fontWeight:'600', fontSize:'17px'}}>
                                                {<Ico_Warning color='error' sx={{ height: '40px', width: '40px' }} />}
                                                <br/>
                                                {` ${workflowState} Workflow`}
                                            </DialogTitle>
                                            <DialogContent>
                                                {`Are you sure you want to ${workflowState.toLowerCase()} this workflow?`}
                                                <DialogActions sx={{ paddingTop: '20px', width:'100%' }}>
                                                    <Button fullWidth variant="outlined" onClick={() => {
                                                        setChangeWorkflowStatus(!changeWorkflowStatus);
                                                    }}>
                                                        Cancel
                                                    </Button>
                                                    <Button fullWidth={true} variant='contained' onClick={(e:any) =>handleConfirm()}>
                                                        {` ${workflowState}`}
                                                    </Button>                                                    
                                                </DialogActions>
                                            </DialogContent>
                                        </Dialog>
                            </Box>
                        </Tooltip>
                        }
                    </Grid>
                    <Grid item xs={3} lg={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pb: 1 }}>
                        {workflow.progress == null || !workflow.status ? null : <TaskProgressPill progress={workflow.progress} />}
                    </Grid>
                </Grid>
                <Collapse in={expand}>
                    { workflow && 
                        <WorkflowTasksGrid 
                            workflow={workflow}
                            project={props.project}
                            refresh={refresh}
                        />
                    }
                </Collapse>                
            </CardContent>
        </Card>
    );
};

export default WorkflowTasksCard;